import {
  Outlet,
  ReactLocation,
  RouteMatch,
  Router,
} from '@tanstack/react-location';
import { useEffect } from 'react';
import useAccessCodeApi from './api/use-access-code-api';
import useAssessmentApi from './api/use-assessment-api';
import AssessmentGatedContainer from './components/assessment-gated-container';
import ExitIntentModal from './components/modal/exit-intent-modal';
import NotificationBar from './components/notification-bar';
import SignupGatedContainer from './components/signup-gated-container';
import SuspensePreloader from './components/suspense-preloader';
import { accessCodes } from './config';
import { useFlexAssessmentContext } from './context/context';
import AssessmentStartPage from './pages/assessment-start-page';
import ErrorPage from './pages/error';
import Landing from './pages/landing/landing';
import LandingContainer from './pages/landing/landing-container';
import { Device } from './types/device';
import { FlexAssessmentContextAction } from './types/flex-assessment-context-action';
import { InAppSearchParams } from './types/in-app-search-params';
import { RegistrationFieldName } from './types/registration-field-name';
import { ProgramType } from './types/types';
import {
  BRAZE_ATTRIBUTES,
  setBrazeAttribute,
  trackPageViewInBraze,
} from './utils/braze-helper';
import { PAGE_URLS } from './utils/constants';
import { PageNotFoundError } from './utils/errors';
import { trackPageViewInGA } from './utils/google-analytics-helper';
import {
  identifyUser,
  registerAccessCodeSuperProperty,
  trackPageViewInMixpanel,
} from './utils/mixpanel-helper';
import { initBridge, useNativeBridge } from './utils/native-bridge';

const location = new ReactLocation();

export default function App(): JSX.Element {
  const { getAccessCodeConfig, getOrgConfig } = useAccessCodeApi();
  const { getAssessment, getAssessmentResults } = useAssessmentApi();
  const { state, dispatch } = useFlexAssessmentContext();
  const { emitAppBootedEvent, exitWebview } = useNativeBridge();

  useEffect(() => {
    setBrazeAttribute(BRAZE_ATTRIBUTES.PROGRAM, ProgramType.flex);
  }, []);

  const trackPageView = (pathname: string): void => {
    trackPageViewInBraze(pathname);
    trackPageViewInGA(pathname);
    trackPageViewInMixpanel(pathname);
  };

  return (
    <Router
      location={location}
      defaultErrorElement={<ErrorPage />}
      routes={[
        {
          path: PAGE_URLS.LANDING,
          element: <LandingContainer />,
          loader: async (routeMatch) => {
            const { access_code: accessCode } = routeMatch.search;
            await getAccessCodeConfig(accessCode);
            return {};
          },
          pendingMs: 1,
          pendingElement: <SuspensePreloader />,
          onTransition: (routeMatch): void => {
            const { access_code: accessCode } = routeMatch.search;
            registerAccessCodeSuperProperty(accessCode as string);
            trackPageView(routeMatch.pathname);
          },
        },
        {
          path: `${PAGE_URLS.PARTNER}/:alias`,
          element: <Landing />,
          loader: async (routeMatch) => {
            const { alias } = routeMatch.params;
            const { access_code: accessCode } = routeMatch.search;
            await Promise.all([
              getOrgConfig(alias),
              getAccessCodeConfig(accessCode),
            ]);
            return {};
          },
          pendingMs: 1,
          pendingElement: <SuspensePreloader />,
          onTransition: (routeMatch): void => {
            const { alias } = routeMatch.params;
            const { access_code: accessCode } = routeMatch.search;
            dispatch({
              type: FlexAssessmentContextAction.setAlias,
              payload: alias,
            });
            registerAccessCodeSuperProperty(accessCode as string);
            trackPageView(routeMatch.pathname);
          },
        },
        {
          path: PAGE_URLS.IN_APP,
          children: [
            {
              id: 'in-app-assessment',
              path: '',
              search: ({
                access_code: accessCode,
                device,
                user_id: userId,
              }: InAppSearchParams): boolean => {
                // Accounting for users who step back and already have the assessment loaded
                if (state.accessCode && state.device && state.userId) {
                  return true;
                }
                const devices = Object.keys(Device).map((k: string): string =>
                  k.toLowerCase()
                );
                return Boolean(
                  accessCode &&
                    userId &&
                    device &&
                    devices.includes(String(device)?.toLowerCase())
                );
              },
              element: () =>
                import('./pages/assessment-intro-page').then((component) => (
                  <component.default />
                )),
              loader: async (routeMatch: RouteMatch) => {
                // Accounting for users who step back and already have the assessment loaded
                if (!state.assessmentId || !state.questionSet) {
                  const { access_code: accessCode, user_id: userId } =
                    routeMatch.search;
                  await getAssessment(accessCode, userId);
                }
                return {};
              },
              onTransition: async (routeMatch: RouteMatch): Promise<void> => {
                const {
                  access_code: accessCode,
                  device,
                  is_onboarding,
                  is_updating, // for backwards compatibility
                  user_id: userId,
                  name,
                }: InAppSearchParams = routeMatch.search;

                const isOnboardingString = is_onboarding?.toString();
                const isUpdatingString = is_updating?.toString();

                let isOnboarding = true;
                if (isOnboardingString) {
                  isOnboarding = isOnboardingString.toLowerCase() === 'true';
                } else if (isUpdatingString) {
                  isOnboarding = is_updating
                    ? isUpdatingString.toLowerCase() !== 'true'
                    : true;
                }

                dispatch({
                  type: FlexAssessmentContextAction.setInAppAssessmentConfig,
                  payload: {
                    isInApp: true,
                    isOnboarding,
                  },
                });
                dispatch({
                  type: FlexAssessmentContextAction.setFieldValue,
                  field: RegistrationFieldName.firstName,
                  value: name,
                  isValid: true,
                });

                await initBridge(device as Device, location, isOnboarding);
                emitAppBootedEvent();
                identifyUser(userId!);
                registerAccessCodeSuperProperty(accessCode as string);
                trackPageView(routeMatch.pathname);
              },
            },
            {
              id: 'in-app-assessment-error',
              path: '',
              search: ({
                access_code: accessCode,
                device,
                user_id: userId,
              }: InAppSearchParams): boolean => {
                const devices = Object.keys(Device).map((k: string): string =>
                  k.toLowerCase()
                );
                return (
                  !accessCode ||
                  !userId ||
                  !device ||
                  !devices.includes(String(device)?.toLowerCase())
                );
              },
              element: <ErrorPage />,
              onTransition: async (routeMatch: RouteMatch): Promise<void> => {
                const { device, is_onboarding } = routeMatch.search;
                const isOnboarding =
                  (is_onboarding as string)?.toLowerCase() === 'true';
                await initBridge(device as Device, location, isOnboarding);
                emitAppBootedEvent();
                setTimeout(() => exitWebview(), 1000);
                trackPageView(routeMatch.pathname);
              },
            },
          ],
        },
        {
          path: PAGE_URLS.ASSESSMENT,
          children: [
            {
              path: '',
              id: 'intro',
              element: <AssessmentStartPage />,
              search: (search) => {
                const { access_code } = search;
                return access_code;
              },
              loader: async (routeMatch) => {
                const { access_code: accessCode } = routeMatch.search;
                await Promise.all([
                  getAccessCodeConfig(accessCode),
                  getAssessment(accessCode),
                ]);
                return {};
              },
              pendingMs: 1,
              pendingElement: <SuspensePreloader />,
              onTransition: (routeMatch): void => {
                const { access_code: accessCode } = routeMatch.search;
                registerAccessCodeSuperProperty(accessCode as string);
                trackPageView(routeMatch.pathname);
              },
            },
            {
              path: '/questions',
              element: () =>
                import('./pages/assessment-questions').then((component) => (
                  <AssessmentGatedContainer>
                    <component.default />
                  </AssessmentGatedContainer>
                )),
              pendingMs: 1,
              pendingElement: <SuspensePreloader />,
              onTransition: (routeMatch): void => {
                trackPageView(routeMatch.pathname);
              },
            },
          ],
        },
        {
          path: PAGE_URLS.B2B_MARKETING_ASSESSMENT,
          element: <AssessmentStartPage />,
          search: (search) => {
            const { access_code } = search;
            return access_code;
          },
          loader: async (routeMatch) => {
            const { access_code: accessCode } = routeMatch.search;
            await Promise.all([
              getAccessCodeConfig(accessCode),
              getAssessment(accessCode),
            ]);
            return {};
          },
          pendingMs: 1,
          pendingElement: <SuspensePreloader />,
          onTransition: (routeMatch): void => {
            dispatch({
              type: FlexAssessmentContextAction.setInB2BCampaignAssessment,
              payload: true,
            });
            const { access_code: accessCode } = routeMatch.search;
            registerAccessCodeSuperProperty(accessCode as string);
            trackPageView(routeMatch.pathname);
          },
        },
        {
          path: PAGE_URLS.PRE_RESULTS_ANIMATION,
          element: () =>
            import('./pages/pre-result-animation').then((component) => (
              <component.default />
            )),
          pendingMs: 1,
          pendingElement: <SuspensePreloader />,
          onTransition: (routeMatch): void => {
            trackPageView(routeMatch.pathname);
          },
        },
        {
          path: PAGE_URLS.RESULTS,
          children: [
            {
              id: 'user-returning-from-email',
              path: '',
              search: (search) => {
                const { accessCode, assessmentId, userEmail } = search;
                return accessCode && assessmentId && userEmail;
              },
              element: () => {
                return import('./pages/results/assessment-results').then(
                  (component) => <component.default />
                );
              },
              loader: async (routeMatch) => {
                const { accessCode, assessmentId } = routeMatch.search;
                await Promise.all([
                  getAccessCodeConfig(accessCode),
                  getAssessmentResults(assessmentId),
                ]);
                return {};
              },
              pendingMs: 1,
              pendingElement: <SuspensePreloader />,
              onTransition: (routeMatch): void => {
                const { accessCode, assessmentId, userEmail } =
                  routeMatch.search;
                registerAccessCodeSuperProperty(accessCode as string);
                dispatch({
                  type: FlexAssessmentContextAction.setFieldValue,
                  field: RegistrationFieldName.email,
                  value: userEmail as string,
                  isValid: true,
                });
                dispatch({
                  type: FlexAssessmentContextAction.setAccessCode,
                  payload: accessCode,
                });
                dispatch({
                  type: FlexAssessmentContextAction.setAssessmentId,
                  payload: assessmentId,
                });
                trackPageView(routeMatch.pathname);
              },
            },
            {
              id: 'in-app-historic-results',
              path: '',
              search: (search) => {
                const {
                  access_code: accessCode,
                  assessment_id: assessmentId,
                  device,
                  user_id: userId,
                } = search;
                return accessCode && assessmentId && device && userId;
              },
              element: () => {
                return import('./pages/results/assessment-results').then(
                  (component) => <component.default />
                );
              },
              loader: async (routeMatch) => {
                const { access_code: accessCode, assessment_id: assessmentId } =
                  routeMatch.search;
                await Promise.all([
                  getAccessCodeConfig(accessCode),
                  getAssessmentResults(assessmentId),
                ]);
                return {};
              },
              pendingMs: 1,
              pendingElement: <SuspensePreloader />,
              onTransition: async (routeMatch): Promise<void> => {
                const {
                  access_code: accessCode,
                  assessment_id: assessmentId,
                  device,
                  user_id: userId,
                  name,
                } = routeMatch.search;
                const isOnboarding = false;

                registerAccessCodeSuperProperty(accessCode as string);
                dispatch({
                  type: FlexAssessmentContextAction.setAccessCode,
                  payload: accessCode,
                });
                dispatch({
                  type: FlexAssessmentContextAction.setAssessmentId,
                  payload: assessmentId,
                });
                dispatch({
                  type: FlexAssessmentContextAction.setEntrancePath,
                  payload: PAGE_URLS.RESULTS,
                });
                dispatch({
                  type: FlexAssessmentContextAction.setInAppAssessmentConfig,
                  payload: {
                    isInApp: true,
                    isOnboarding: false,
                  },
                });
                dispatch({
                  type: FlexAssessmentContextAction.setFieldValue,
                  field: RegistrationFieldName.firstName,
                  value: name as string,
                  isValid: true,
                });

                await initBridge(device as Device, location, isOnboarding);
                emitAppBootedEvent();
                identifyUser(userId! as string);
                trackPageView(routeMatch.pathname);
              },
            },
            {
              id: 'standard-assessment-flow',
              path: '',
              search: (search) => {
                const { accessCode, assessmentId, userEmail } = search;
                return !accessCode || !assessmentId || !userEmail;
              },
              element: () => {
                return import('./pages/results/assessment-results').then(
                  (component) => (
                    <AssessmentGatedContainer>
                      <component.default />
                    </AssessmentGatedContainer>
                  )
                );
              },
              pendingMs: 1,
              pendingElement: <SuspensePreloader />,
              onTransition: (routeMatch): void => {
                trackPageView(routeMatch.pathname);
              },
            },
          ],
        },
        {
          path: PAGE_URLS.SIGNUP,
          element: () =>
            import('./pages/signup/signup').then((component) => (
              <SignupGatedContainer>
                <component.default />
              </SignupGatedContainer>
            )),
          pendingMs: 1,
          pendingElement: <SuspensePreloader />,
          onTransition: (routeMatch): void => {
            trackPageView(routeMatch.pathname);
          },
        },
        {
          path: PAGE_URLS.SUCCESS,
          element: () =>
            import('./pages/account-created').then((component) => (
              <component.default />
            )),
          loader: async (routeMatch) => {
            return {
              ...routeMatch.search,
            };
          },
          pendingMs: 1,
          pendingElement: <SuspensePreloader />,
          onTransition: (routeMatch): void => {
            trackPageView(routeMatch.pathname);
          },
        },
        {
          path: PAGE_URLS.ELIGIBILITY_REVIEW,
          element: () =>
            import('./pages/eligibility-review').then((component) => (
              <AssessmentGatedContainer>
                <component.default />
              </AssessmentGatedContainer>
            )),
          loader: async (routeMatch) => {
            return {
              ...routeMatch.search,
            };
          },
          pendingMs: 1,
          pendingElement: <SuspensePreloader />,
          onTransition: (routeMatch): void => {
            trackPageView(routeMatch.pathname);
          },
        },
        {
          path: PAGE_URLS.UNDER_CONSTRUCTION,
          element: () =>
            import('./pages/under-construction').then((component) => (
              <component.default />
            )),
          pendingMs: 1,
          pendingElement: <SuspensePreloader />,
          onTransition: (routeMatch): void => {
            trackPageView(routeMatch.pathname);
          },
        },
        {
          path: PAGE_URLS.WAITING_LIST,
          element: () =>
            import('./pages/waiting-list').then((component) => (
              <component.default />
            )),
          pendingMs: 1,
          pendingElement: <SuspensePreloader />,
          onTransition: (routeMatch): void => {
            trackPageView(routeMatch.pathname);
          },
        },
        {
          path: PAGE_URLS.TERMS_AND_CONDITIONS,
          element: () =>
            import('./pages/terms-conditions').then((component) => (
              <component.default />
            )),
          pendingMs: 1,
          pendingElement: <SuspensePreloader />,
          onTransition: (routeMatch): void => {
            trackPageView(routeMatch.pathname);
          },
        },
        {
          path: PAGE_URLS.PRIVACY_POLICY,
          element: () =>
            import('./pages/privacy-policy').then((component) => (
              <component.default />
            )),
          pendingMs: 1,
          pendingElement: <SuspensePreloader />,
          onTransition: (routeMatch): void => {
            trackPageView(routeMatch.pathname);
          },
        },
        {
          path: PAGE_URLS.ERROR,
          element: () =>
            import('./pages/error').then((component) => <component.default />),
          loader: async (routeMatch) => {
            return {
              ...routeMatch.search,
            };
          },
          pendingMs: 1,
          pendingElement: <SuspensePreloader />,
          onTransition: (routeMatch): void => {
            trackPageView(routeMatch.pathname);
          },
        },
        {
          path: PAGE_URLS.CATCH_ALL,
          element: () =>
            import('./pages/error').then((component) => <component.default />),
          pendingMs: 1,
          pendingElement: <SuspensePreloader />,
          onTransition: (routeMatch): void => {
            const path = routeMatch.matchLoader?.location.pathname ?? 'unknown';
            new PageNotFoundError(path);
          },
        },
      ]}
    >
      <NotificationBar />
      <Outlet />
      {state.accessCode === accessCodes.dtc &&
        location.current.pathname === PAGE_URLS.LANDING && <ExitIntentModal />}
    </Router>
  );
}
